import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Coins, Snowflake, ShoppingBag, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { getChips, spendChips, getFreezes, setFreezes, SHOP_ITEMS } from "@/lib/chips";
import { sfx } from "@/lib/sfx";
import { fireConfetti } from "@/lib/confetti";
import { syncPlayerState } from "@/lib/playerSync";
import { startCheckout, getEmail } from "@/lib/pro";
import { SKINS, setCurrentSkinId } from "@/lib/skins";
import { HANDLE_GEMS, getGemInventory, markGemOwned } from "@/lib/achievementsExtra";

export default function ShopDialog({ open, onOpenChange }) {
  const [chips, setChips] = useState(() => getChips());
  const [freezes, setLocalFreezes] = useState(() => getFreezes());
  const [busy, setBusy] = useState(false);
  const [rotation, setRotation] = useState(null);
  const [rotationOwned, setRotationOwned] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [ownedGems, setOwnedGems] = useState(() => getGemInventory());

  // Tick every 30s so the countdown label updates while the dialog is open
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [open]);

  const countdown = useMemo(() => {
    if (!rotation || !rotation.week_start) return "";
    const next = new Date(rotation.week_start + "T00:00:00Z");
    next.setUTCDate(next.getUTCDate() + 7);
    const remaining = next.getTime() - nowTick;
    if (remaining <= 0) return "resets soon";
    const d = Math.floor(remaining / 86400000);
    const h = Math.floor((remaining % 86400000) / 3600000);
    const m = Math.floor((remaining % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  }, [rotation, nowTick]);

  useEffect(() => {
    if (open) {
      setChips(getChips());
      setLocalFreezes(getFreezes());
      // Load weekly featured skin rotation
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/shop/rotation`)
        .then((res) => {
          const r = res.data;
          if (!r || !r.skin_id) return;
          // Enrich with catalog metadata if backend didn't send it
          const catalog = SKINS.find((s) => s.id === r.skin_id);
          setRotation({
            skin_id: r.skin_id,
            week_start: r.week_start,
            name: r.name || catalog?.name || r.skin_id.toUpperCase(),
            color: r.color || catalog?.color || "#FFD400",
            glow: r.glow || catalog?.glow || "rgba(255,212,0,0.65)",
            featured_handle: r.featured_handle,
            chip_price: r.chip_price || 400,
            source: r.source,
          });
          const seen = JSON.parse(localStorage.getItem("ppe_rotation_owned_v1") || "[]");
          setRotationOwned(seen.includes(`${r.week_start}:${r.skin_id}`));
        })
        .catch(() => setRotation(null));
    }
  }, [open]);

  const buyWithChips = (item) => {
    const res = spendChips(item.price);
    if (!res.ok) {
      toast.error(`Need ${item.price - res.have} more chips`);
      return;
    }
    sfx.pickup();
    if (item.kind === "freeze") {
      setFreezes(getFreezes() + 1);
      setLocalFreezes(getFreezes());
    }
    setChips(getChips());
    fireConfetti({ color: "#FFD400", glow: "rgba(255,212,0,0.9)", count: 40 });
    toast.success(`GOT · ${item.label}`);
    syncPlayerState();
  };

  const buyWithMoney = async () => {
    const email = getEmail();
    if (!email) return toast.error("Enter your email in the paywall first");
    setBusy(true);
    try {
      const { checkout_url } = await startCheckout({ lookup_key: "streak_freeze", email });
      window.location.href = checkout_url;
    } catch {
      toast.error("Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shop-dialog" className="max-w-2xl bg-black/85 backdrop-blur-2xl border border-white/10 text-white rounded-none p-0">
        <div className="p-6 border-b border-white/10 bg-gradient-to-br from-yellow-500/5 to-transparent">
          <DialogHeader className="text-left">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FFD400]">
              <ShoppingBag size={14} /> NEON SHOP
            </div>
            <DialogTitle className="font-display text-3xl tracking-tighter text-white">
              Spend your <span className="text-[#FFD400]" style={{ textShadow: "0 0 22px rgba(255,212,0,0.5)" }}>chips</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-6 mt-4">
            <div className="flex items-center gap-2 text-lg font-mono" data-testid="chip-balance">
              <Coins size={16} className="text-[#FFD400]" />
              <span className="text-[#FFD400] font-bold" style={{ textShadow: "0 0 12px rgba(255,212,0,0.6)" }}>{chips}</span>
              <span className="text-[10px] tracking-[0.3em] text-white/50">CHIPS</span>
            </div>
            <div className="flex items-center gap-2 text-lg font-mono" data-testid="freeze-balance">
              <Snowflake size={16} className="text-cyan-300" />
              <span className="text-cyan-300 font-bold">{freezes}</span>
              <span className="text-[10px] tracking-[0.3em] text-white/50">FREEZES</span>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-3 max-h-[55vh] overflow-y-auto">
          {rotation && (
            <div
              data-testid="shop-rotation-card"
              className="border p-4"
              style={{ borderColor: rotation.color, boxShadow: `0 0 22px ${rotation.glow}` }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={12} style={{ color: rotation.color }} />
                <div className="text-[10px] tracking-[0.3em]" style={{ color: rotation.color }}>WEEKLY FEATURED BUNDLE</div>
                {countdown && (
                  <div
                    data-testid="shop-rotation-countdown"
                    className="ml-auto text-[10px] tracking-[0.3em] text-white/50"
                  >
                    RESETS · {countdown}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 border flex items-center justify-center"
                  style={{ borderColor: rotation.color, boxShadow: `inset 0 0 24px ${rotation.glow}` }}
                >
                  <div
                    className="w-6 h-6"
                    style={{ background: rotation.color, boxShadow: `0 0 18px ${rotation.glow}` }}
                  />
                </div>
                <div className="flex-1">
                  <div className="text-sm tracking-[0.3em] text-white">{rotation.name}</div>
                  <div className="text-[10px] tracking-widest text-white/50 mt-0.5">
                    {rotation.source === "top_wagerer" && rotation.featured_handle
                      ? `Worn by this week's top wagerer · ${rotation.featured_handle}`
                      : "This week's rotating cosmetic"}
                  </div>
                </div>
                <button
                  data-testid="shop-buy-rotation"
                  disabled={rotationOwned}
                  onClick={() => {
                    if (rotationOwned) return;
                    const res = spendChips(rotation.chip_price);
                    if (!res.ok) {
                      toast.error(`Need ${rotation.chip_price - res.have} more chips`);
                      return;
                    }
                    sfx.pickup();
                    setChips(getChips());
                    // Persist the ownership token (immune to catalog unlock() gate)
                    const seen = JSON.parse(localStorage.getItem("ppe_rotation_owned_v1") || "[]");
                    const wk = `${new Date().toISOString().slice(0, 10)}:${rotation.skin_id}`;
                    seen.push(wk);
                    localStorage.setItem("ppe_rotation_owned_v1", JSON.stringify(seen));
                    // Try to equip it if in catalog and already unlocked
                    setCurrentSkinId(rotation.skin_id);
                    setRotationOwned(true);
                    fireConfetti({ color: rotation.color, glow: rotation.glow, count: 60 });
                    toast.success(`OWNED · ${rotation.name}`);
                    syncPlayerState();
                  }}
                  className="px-4 py-2 border transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40"
                  style={{
                    borderColor: rotation.color,
                    color: rotationOwned ? "rgba(255,255,255,0.4)" : rotation.color,
                  }}
                >
                  {rotationOwned ? "OWNED" : `${rotation.chip_price} CHIPS`}
                </button>
              </div>
            </div>
          )}

          {/* Handle Gems — cosmetic prefix on leaderboards */}
          <div className="border border-white/10 p-4" data-testid="shop-handle-gems">
            <div className="text-[10px] tracking-[0.3em] text-[#F0ABFC] mb-3">HANDLE GEMS · LEADERBOARD FLAIR</div>
            <div className="grid grid-cols-3 gap-2">
              {HANDLE_GEMS.map((g) => {
                const count = ownedGems[g.id] || 0;
                return (
                  <button
                    key={g.id}
                    data-testid={`shop-buy-gem-${g.id}`}
                    onClick={() => {
                      const res = spendChips(g.price);
                      if (!res.ok) {
                        toast.error(`Need ${g.price - res.have} more chips`);
                        return;
                      }
                      sfx.pickup();
                      markGemOwned(g.id);
                      setOwnedGems(getGemInventory());
                      setChips(getChips());
                      fireConfetti({ color: g.color, glow: `${g.color}66`, count: 40 });
                      toast.success(`GEM ADDED · ${g.symbol} ${g.name}${count > 0 ? ` (×${count + 1})` : ""}`);
                      syncPlayerState();
                    }}
                    className="border p-3 text-center transition-colors"
                    style={{ borderColor: g.color, boxShadow: `0 0 12px ${g.color}55` }}
                  >
                    <div className="text-2xl" style={{ color: g.color, textShadow: `0 0 12px ${g.color}` }}>{g.symbol}</div>
                    <div className="text-[10px] tracking-[0.3em] mt-1" style={{ color: g.color }}>
                      {g.name}
                    </div>
                    <div className="text-[10px] text-white/50 mt-1">{g.price} CHIPS{count > 0 ? ` · owned ×${count}` : ""}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {SHOP_ITEMS.map((item) => (
            <div key={item.id} data-testid={`shop-item-${item.id}`} className="flex items-center gap-4 border border-white/10 p-4">
              <div className="w-10 h-10 flex items-center justify-center border border-[#FFD400] text-[#FFD400]">
                {item.kind === "freeze" ? <Snowflake size={16} /> : <Coins size={16} />}
              </div>
              <div className="flex-1">
                <div className="text-xs tracking-[0.3em] text-white">{item.label}</div>
                <div className="text-[10px] tracking-widest text-white/50 mt-0.5">{item.desc}</div>
              </div>
              <button
                data-testid={`shop-buy-${item.id}`}
                onClick={() => buyWithChips(item)}
                className="px-4 py-2 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors text-[10px] tracking-[0.3em]"
              >
                {item.price} CHIPS
              </button>
            </div>
          ))}
          {/* Cash-money option for streak freeze */}
          <div className="mt-6 border border-cyan-300/40 p-4" data-testid="shop-cash-freeze">
            <div className="text-[10px] tracking-[0.3em] text-cyan-300 mb-2 flex items-center gap-2">
              <Snowflake size={12} /> OR PAY CASH — INSTANT
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs tracking-[0.3em] text-white">Streak Freeze · $0.99</div>
                <div className="text-[10px] tracking-widest text-white/50 mt-0.5">Skip the grind — one-tap protection</div>
              </div>
              <button
                data-testid="shop-buy-freeze-cash"
                disabled={busy}
                onClick={buyWithMoney}
                className="px-4 py-2 border border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40"
              >
                BUY · $0.99
              </button>
            </div>
          </div>
          <p className="text-[10px] tracking-[0.3em] text-white/40 text-center mt-4">
            <CheckCircle2 size={10} className="inline mr-1 text-[#39FF14]" />
            Earn ~1 chip per 10 points. Win bonus: +25.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
